import { useState, useEffect } from 'react';
import { edcBuildBannerText } from './edcBuildBanner';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  ArrowRight,
} from 'lucide-react';
import { onSnapshot, query, where } from 'firebase/firestore';
import RequirementsDesignCard from '../../components/reqDesign/RequirementsDesignCard';
import TestingQualificationCard from '../../components/testing/TestingQualificationCard';
import { dataManagementDoc, artifactsCollection } from '../../constants/firestorePaths';

// ============================================================
// SDLC Document Hierarchy
// ============================================================

interface SDLCDocument {
  id: string;
  name: string;
  shortName: string;
  description: string;
  icon: React.ReactNode;
  status: 'available' | 'coming_soon';
  category: 'requirements' | 'testing';
  dependencies: string[]; // IDs of prerequisite documents
  children?: string[];    // IDs of documents that depend on this one
}

// All study-build documents are now rendered by <RequirementsDesignCard />
// (URS/FRS/SDS/ADS/ATDS/SACS/IIS + standalone sub-docs including the
// interactive Database Schema editor) and <TestingQualificationCard />.
// The SDLC_DOCUMENTS array is intentionally empty — the docsByCategory
// loop below renders nothing, but CATEGORY_CONFIG / CATEGORIES_ORDER are
// kept to drive the SDLC flow indicator badges at the top of the page.
const SDLC_DOCUMENTS: SDLCDocument[] = [];

const CATEGORY_CONFIG = {
  requirements: {
    label: 'Requirements',
    color: 'indigo',
    bgClass: 'bg-indigo-50 border-indigo-200',
    textClass: 'text-indigo-700',
    iconBg: 'bg-indigo-100',
    iconText: 'text-indigo-600',
    accentBorder: 'border-l-indigo-400',
  },
  testing: {
    label: 'Testing & Qualification',
    color: 'emerald',
    bgClass: 'bg-emerald-50 border-emerald-200',
    textClass: 'text-emerald-700',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    accentBorder: 'border-l-emerald-400',
  },
};

const CATEGORIES_ORDER: Array<keyof typeof CATEGORY_CONFIG> = [
  'requirements', 'testing',
];

// ============================================================
// Component
// ============================================================

export default function EDCBuild() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(CATEGORIES_ORDER)
  );

  // Track Data Dictionary state + approved-CRF count so we can show a
  // banner explaining what the DD currently represents. The DD is the
  // single source of truth for downstream Study Build documents — if it's
  // out of date with the CRF Builder, every doc generated from it will
  // also be out of date.
  const [ddStats, setDdStats] = useState<{
    formCount: number;
    fieldCount: number;
  } | null>(null);
  const [crfStats, setCrfStats] = useState<{
    total: number;
    approved: number;
  } | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const unsubDd = onSnapshot(
      dataManagementDoc(projectId, 'dataDictionary'),
      (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setDdStats({
            formCount: typeof d.formCount === 'number' ? d.formCount : 0,
            fieldCount: typeof d.fieldCount === 'number' ? d.fieldCount : 0,
          });
        } else {
          setDdStats(null);
        }
      }
    );
    const unsubCrf = onSnapshot(
      query(
        artifactsCollection(projectId),
        where('type', '==', 'crf')
      ),
      (snapshot) => {
        let approved = 0;
        snapshot.forEach((d) => {
          const s = d.data().status;
          if (s === 'approved' || s === 'final') approved++;
        });
        setCrfStats({ total: snapshot.size, approved });
      }
    );
    return () => {
      unsubDd();
      unsubCrf();
    };
  }, [projectId]);

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const getDocById = (id: string) => SDLC_DOCUMENTS.find(d => d.id === id);

  // Skip categories that have no documents (e.g. requirements after the
  // consolidation move) — they would otherwise render an empty header.
  const docsByCategory = CATEGORIES_ORDER
    .map(cat => ({
      category: cat,
      config: CATEGORY_CONFIG[cat],
      docs: SDLC_DOCUMENTS.filter(d => d.category === cat),
    }))
    .filter(group => group.docs.length > 0);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          Study Build Documentation
        </h1>
        <p className="text-gray-600 mt-2 leading-relaxed max-w-3xl">
          Documentation that verifies <strong>this study</strong> was built correctly
          inside your EDC (REDCap, Medidata, Veeva, Oracle, etc.). These documents
          cover the study-specific CRFs, edit checks, derivations, visit schedule,
          user roles, and integrations &mdash; not the EDC platform itself. Your EDC
          vendor supplies the platform validation package separately.
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Looking for your study&rsquo;s data management deliverables? Open <a
            href="#" onClick={(e) => { e.preventDefault(); if (projectId) navigate(`/project/${projectId}/data-management`); }}
            className="text-primary-600 hover:underline"
          >Study Documents</a>.
        </p>
      </div>

      {/* Data Dictionary coverage banner — the DD is the single source of
          truth for every document generated below, so users need to know
          how much of the current CRF Builder state it captures. */}
      {(() => {
        if (!ddStats) {
          if (!crfStats || crfStats.approved === 0) return null;
          return (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              The Data Dictionary has not been generated yet.{' '}
              {crfStats.approved} of {crfStats.total} case report{' '}
              {crfStats.total === 1 ? 'form is' : 'forms are'} approved and ready to be included.{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (projectId) navigate(`/project/${projectId}/data-management`);
                }}
                className="font-medium underline hover:text-amber-900"
              >
                Generate it in Study Documents
              </a>{' '}
              before generating the documents below.
            </div>
          );
        }
        const banner = edcBuildBannerText(ddStats, crfStats);
        const stale = banner.stale;
        return (
          <div
            className={
              stale
                ? 'p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800'
                : 'p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800'
            }
          >
            {banner.summary}{' '}
            {stale ? (
              <>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (projectId) navigate(`/project/${projectId}/data-management`);
                  }}
                  className="font-medium underline hover:text-amber-900"
                >
                  Regenerate the Data Dictionary
                </a>{' '}
                so the documents below pick up the latest forms.
              </>
            ) : (
              <>Every document below is generated from this Data Dictionary.</>
            )}
          </div>
        );
      })()}

      {/* SDLC Flow Indicator */}
      <div className="flex items-center justify-center gap-2 py-3">
        {CATEGORIES_ORDER.map((cat, i) => (
          <div key={cat} className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${CATEGORY_CONFIG[cat].bgClass} ${CATEGORY_CONFIG[cat].textClass}`}>
              {CATEGORY_CONFIG[cat].label}
            </span>
            {i < CATEGORIES_ORDER.length - 1 && (
              <ArrowRight className="h-4 w-4 text-gray-300" />
            )}
          </div>
        ))}
      </div>

      {/* Needs-review queue retired from this page (inc5 step F): the
          pipeline-family decisions render on the Verification tab in
          customer language — nothing is actionable in two places. */}

      {/* ============================================================ */}
      {/* Consolidated Requirements & Design card                       */}
      {/* ============================================================ */}
      {projectId && <RequirementsDesignCard projectId={projectId} />}

      {/* ============================================================ */}
      {/* Remaining categories (Design / Planning / Testing)            */}
      {/* ============================================================ */}
      <div className="space-y-3">
        {docsByCategory.map(({ category, config, docs }) => (
        <div key={category} className="space-y-2">
          {/* Category Header */}
          <button
            onClick={() => toggleCategory(category)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border ${config.bgClass} transition-colors hover:opacity-90`}
          >
            {expandedCategories.has(category) ? (
              <ChevronDown className={`h-4 w-4 ${config.textClass}`} />
            ) : (
              <ChevronRight className={`h-4 w-4 ${config.textClass}`} />
            )}
            <span className={`font-semibold text-sm ${config.textClass}`}>
              {config.label}
            </span>
            <span className={`text-xs ${config.textClass} opacity-60`}>
              {docs.length} document{docs.length !== 1 ? 's' : ''}
            </span>
            <div className="flex-1" />
            <span className={`text-xs ${config.textClass} opacity-60`}>
              {docs.filter(d => d.status === 'available').length} available
            </span>
          </button>

          {/* Documents in this category */}
          {expandedCategories.has(category) && (
            <div className="space-y-2 ml-4">
              {docs.map(doc => {
                const isExpanded = expandedDoc === doc.id;
                const deps = doc.dependencies.map(getDocById).filter(Boolean);

                return (
                  <div
                    key={doc.id}
                    className={`card border-l-4 ${config.accentBorder} overflow-hidden transition-all duration-200 ${
                      isExpanded ? 'ring-1 ring-gray-200' : ''
                    }`}
                  >
                    {/* Document Row */}
                    <button
                      onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
                      className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50/50 transition-colors"
                    >
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${config.iconBg}`}>
                        <div className={config.iconText}>{doc.icon}</div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 text-sm">{doc.name}</span>
                          <span className="text-[10px] font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                            {doc.shortName}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{doc.description}</p>
                      </div>

                      {doc.status === 'available' ? (
                        <span className="badge-success text-[10px]">Available</span>
                      ) : (
                        <span className="badge-neutral text-[10px]">Coming Soon</span>
                      )}

                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      )}
                    </button>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <div className="px-5 pb-4 pt-0 border-t border-gray-100 animate-fade-in">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                          {/* Description */}
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Description</h4>
                            <p className="text-sm text-gray-700 leading-relaxed">{doc.description}</p>
                          </div>

                          {/* Dependencies & Outputs */}
                          <div className="space-y-3">
                            {deps.length > 0 && (
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Depends On</h4>
                                <div className="flex flex-wrap gap-1.5">
                                  {deps.map(dep => (
                                    <button
                                      key={dep!.id}
                                      onClick={(e) => { e.stopPropagation(); setExpandedDoc(dep!.id); }}
                                      className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                                    >
                                      {dep!.shortName}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {doc.children && doc.children.length > 0 && (
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Feeds Into</h4>
                                <div className="flex flex-wrap gap-1.5">
                                  {doc.children.map(childId => {
                                    const child = getDocById(childId);
                                    if (!child) return null;
                                    return (
                                      <button
                                        key={childId}
                                        onClick={(e) => { e.stopPropagation(); setExpandedDoc(childId); }}
                                        className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                                      >
                                        {child.shortName}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action / Inline Panel */}
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          {doc.status === 'available' ? (
                            <p className="text-sm text-gray-500">
                              Click to view the {doc.name} section.
                            </p>
                          ) : (
                            <p className="text-sm text-gray-400 italic">
                              This document will be available in a future update.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        ))}
      </div>

      {/* ============================================================ */}
      {/* Testing & Qualification — own card matching the Combined      */}
      {/* documents row style (status badge + inline Generate/Download */}
      {/* buttons, no chevron-expand-to-do-anything pattern).           */}
      {/* ============================================================ */}
      {projectId && <TestingQualificationCard projectId={projectId} />}

    </div>
  );
}
